import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { GoogleMap } from "@react-google-maps/api";
import { type Location } from "@src/appV2/lib/GoogleMaps";

import { useGetAddressByGeoCode } from "../../lib/GoogleMaps/api/useGetAddressByGeoCode";
import { OpenGeoLocationProvider } from "../OpenGeoLocation/useOpenGeoLocation";
import { GoogleMapCustomMarkerWrapper } from "./GoogleMapCustomMarkerWrapper";
import { GoogleMapErrorBoundary } from "./GoogleMapErrorBoundary";
import { MapMarkerActive } from "./MapMarkerActive";
import { MapWithAddressAction } from "./MapWithAddressAction";

interface MapWithAddressProps {
  workplaceId: string;
  workplaceName?: string;
  location: Location;
  workplaceFormattedAddress?: string;
  readonly?: boolean;
  additionalContent?: JSX.Element;
  obscureAddressConfig?: {
    shouldObscureAddress: boolean;
    obscuredAddress?: string;
  };
}

export function MapWithAddress(props: MapWithAddressProps) {
  const {
    workplaceId,
    workplaceName,
    location,
    // eslint-disable-next-line unicorn/prevent-abbreviations
    workplaceFormattedAddress: propAddress,
    readonly,
    additionalContent,
    obscureAddressConfig,
  } = props;

  const { shouldObscureAddress = false, obscuredAddress } = obscureAddressConfig ?? {};

  const { data: geoCodedAddress, isSuccess: isGeocodeSuccess } = useGetAddressByGeoCode(
    isDefined(propAddress) ? undefined : location
  );

  const displayedAddress = shouldObscureAddress
    ? obscuredAddress
    : (propAddress ?? geoCodedAddress?.formatted);
  /**
   * Formats the address to be displayed in two lines
   */
  const formatAddress = (address?: string) => {
    if (!address) {
      return "";
    }

    const split = address.split(", ") ?? [];

    return `${split.slice(0, 2).join(", ")}\n${split.slice(2).join(", ")}`;
  };

  return (
    <Box sx={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
      <GoogleMapErrorBoundary>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          options={{
            center: location,
            disableDefaultUI: true,
            // Cooperative gesture handling works better if we put this inside a scroll view as
            // scroll events should not interfere with the map gestures
            gestureHandling: readonly ? "none" : "cooperative",
            clickableIcons: !readonly,
          }}
          onLoad={(map) => {
            // TODO: Investigate and abstract zoom values into an enum like `MapZoomLevel`
            map.setZoom(15);
          }}
        >
          <GoogleMapCustomMarkerWrapper position={location}>
            <MapMarkerActive />
          </GoogleMapCustomMarkerWrapper>
        </GoogleMap>
      </GoogleMapErrorBoundary>

      {(isDefined(propAddress) || isGeocodeSuccess) && (
        <OpenGeoLocationProvider
          location={location}
          name={workplaceName}
          formattedAddress={propAddress ?? geoCodedAddress?.formatted}
        >
          <MapWithAddressAction workplaceId={workplaceId}>
            <Stack spacing={1}>
              <Text variant="body2">{formatAddress()}</Text>
              {additionalContent}
            </Stack>
            <Text variant="body2">{formatAddress(displayedAddress)}</Text>
          </MapWithAddressAction>
        </OpenGeoLocationProvider>
      )}
    </Box>
  );
}
